'use client';

import React from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { 
  Text, 
  Heading 
} from '../ui/design-system';

interface LegalHeroProps {
  title: string;
  description?: string;
  imageSrc?: string;
  imageAlt?: string;
  lastUpdated?: string;
  className?: string;
}

/**
 * LegalHero Component
 * 
 * A special hero component for legal pages that maintains consistency with
 * main page heroes but with a more minimal approach suitable for legal content.
 */
export function LegalHero({
  title,
  description,
  imageSrc,
  imageAlt = "Legal page banner",
  lastUpdated,
  className,
}: LegalHeroProps) {
  // Animation variants
  const heroVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <section 
      className={cn(
        "relative overflow-hidden py-24 md:py-36 lg:py-40 w-full",
        className
      )}
    >
      {/* Background with banner image */}
      <div className="absolute inset-0 w-full h-full -z-10">
        {imageSrc ? (
          <div className="relative w-full h-full">
            <Image
              src={imageSrc}
              alt={imageAlt}
              fill
              priority
              className="object-cover"
              sizes="100vw"
              quality={90}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/50 dark:from-gray-900/95 dark:via-gray-900/80 dark:to-gray-900/50" />
          </div>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-sky-200 via-blue-100 to-indigo-200 dark:bg-gradient-to-br dark:from-slate-900 dark:via-neutral-800 dark:to-slate-950">
            {/* Abstract Shapes - Light Mode */}
            <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-sky-300/20 rounded-full blur-3xl animate-pulse-slow -z-10 dark:hidden"></div>
            <div className="absolute bottom-1/4 right-1/4 w-1/3 h-1/3 bg-blue-200/30 rounded-full blur-2xl animate-pulse-slower -z-10 dark:hidden"></div>
            <div className="absolute top-1/3 right-1/5 w-1/4 h-1/4 bg-indigo-300/10 rounded-full blur-3xl animate-pulse-slowest -z-10 dark:hidden"></div>
            {/* Abstract Shapes - Dark Mode */}
            <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-sky-700/10 rounded-full blur-3xl animate-pulse-slow -z-10 hidden dark:block"></div>
            <div className="absolute bottom-1/4 right-1/4 w-1/3 h-1/3 bg-blue-600/20 rounded-full blur-2xl animate-pulse-slower -z-10 hidden dark:block"></div>
            <div className="absolute top-1/3 right-1/5 w-1/4 h-1/4 bg-indigo-500/5 rounded-full blur-3xl animate-pulse-slowest -z-10 hidden dark:block"></div>
          </div>
        )}
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Left-aligned content similar to Industries page */}
        <motion.div
          className="max-w-7xl text-left"
          initial="hidden"
          animate="visible"
          variants={heroVariants}
        >
          <motion.div variants={itemVariants}>
            <div className="w-full max-w-full overflow-hidden">
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-primary-600 dark:text-primary-300 mb-6"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto',
                  lineHeight: 1.15,
                }}
              >
                {title}
              </h1>
            </div>
          </motion.div>
          
          {description && (
            <motion.div variants={itemVariants}>
              <Text 
                variant="body-large"
                color="secondary"
                className="text-gray-700 dark:text-gray-300 mt-6 text-lg md:text-xl max-w-4xl"
              >
                {description}
              </Text>
            </motion.div>
          )}
          
          <motion.div variants={itemVariants} className="mt-10 flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-4 sm:space-y-0">
            {lastUpdated && (
              <Text 
                variant="caption"
                color="muted"
                className="text-gray-500 dark:text-gray-300"
              >
                Last updated: May 26, 2025
              </Text>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
} 